import React, { useCallback, useEffect, useState } from "react";
import { Release, ReleaseResponse, CustomJwtPayload } from "../../models";
import { Button, Col, Form, Row, Table } from "react-bootstrap";
import { ReleaseTableRow } from "../ReleaseTableRow";
import { HTTP_CLIENT } from "../../hooks";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { SubmitNewReleaseModal } from "../SubmitNewReleaseModal";
import { SubmitNewBundleModal } from "../SubmitNewBundleModal";
import { ReleaseNotesModal } from "../ReleaseNotesModal";
import { GenerateManifestModal } from "../GenerateManifestModal";
import { toast } from "react-toastify";
import { ProductLine } from "../FormFields";
import { COMPONENT_DEFAULT, COMPONENT_BUNDLE, DATA_TYPE_BUNDLE_MANIFEST } from "../../utils";
import { Md5 } from "ts-md5";
import { jwtDecode } from "jwt-decode";

const PATH_PREFIX = "/api/v2/releases";

interface Props {
  jwt: string | null;
}

export const Releases = ({ jwt }: Props) => {
  const [model, setModel] = useState<number>(COMPONENT_DEFAULT);
  const [releases, setReleases] = useState<Release[]>();
  const [loading, setLoading] = useState(true);
  const [showSubmitNewReleaseModal, setShowSubmitNewReleaseModal] = useState(false);
  const [showSubmitNewBundleModal, setShowSubmitNewBundleModal] = useState(false);
  const [showReleaseNotesModal, setShowReleaseNotesModal] = useState(false);
  const [showGenerateManifestModal, setShowGenerateManifestModal] = useState(false);
  const [releaseToEdit, setReleaseToEdit] = useState<Release>();

  const updateList = useCallback(async (model: number) => {
    try {
      const releasesResponse = await HTTP_CLIENT.get<ReleaseResponse>({
        path: `${PATH_PREFIX}/search/findByModel`,
        query: { model },
      });
      setReleases(releasesResponse._embedded.releases);
    } catch (e: any) {
      console.error(e.stack);
      setReleases(undefined);
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Errors are handled inside the call to updateList()
    void updateList(model);
  }, [updateList, model]);

  const updateReleaseNotes = async (releaseNotes: string | null) => {
    try {
      await HTTP_CLIENT.patch({
        path: `${PATH_PREFIX}/${releaseToEdit!.id}`,
        headers: { Authorization: `Bearer ${jwt}` },
        body: { releaseNotes },
      });
      await updateList(model);
    } catch (e: any) {
      console.error(e.stack);
      toast.error("Failed to save release. See console for details");
    }
  };

  const submitNewBundle = async (bundleJson: File | undefined, version: string | undefined) => {
    if (!bundleJson || version === "") return;

    const arrayBuffer = await new Promise<ArrayBuffer>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = reject;
      reader.readAsArrayBuffer(bundleJson);
    });
    const blobHash = Md5.hashStr(
      Array.from(new Uint8Array(arrayBuffer))
        .map(byte => String.fromCharCode(byte))
        .join("")
    );
    const blobBase64 = btoa(
      Array.from(new Uint8Array(arrayBuffer))
        .map(byte => String.fromCharCode(byte))
        .join("")
    );

    // POST binary blob.
    await HTTP_CLIENT.post({
      path: "/api/v2/binaryBlobs",
      headers: { Authorization: `Bearer ${jwt}` },
      body: {
        hash: blobHash,
        hashAlgorithm: "MD5",
        data: blobBase64,
        dataType: "/dataTypes/" + DATA_TYPE_BUNDLE_MANIFEST,
      },
    });

    // POST Release
    const time = new Date().toISOString();
    const releaseResponse = await HTTP_CLIENT.post({
      path: PATH_PREFIX,
      headers: { Authorization: `Bearer ${jwt}` },
      body: {
        model: "/modelTypes/" + COMPONENT_BUNDLE,
        version: version,
        buildAuthor: jwtDecode<CustomJwtPayload>(jwt || "").sub!,
        buildTime: time,
        uploadTime: time,
        buildUrl: "N/A",
        vcsCommit: "N/A",
        pinned: false
      },
    });

    // POST Artifact Map
    await HTTP_CLIENT.post({
      path: "/api/v2/artifactFileMaps",
      headers: { Authorization: `Bearer ${jwt}` },
      body: {
        release: releaseResponse.id,
        binary_blob: blobHash,
      },
    });
  };

  return (
    <>
      <Row>
        <Col>
          <h1>
            Releases
            {jwt && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  if (model === COMPONENT_BUNDLE) {
                  setShowSubmitNewBundleModal(true);
                  } else {
                  setShowSubmitNewReleaseModal(true);
                  }
                }}
                >
                <FontAwesomeIcon icon={faPlus} />
              </Button>
            )}
            {model === COMPONENT_BUNDLE && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowGenerateManifestModal(true)}
              >
                Generate Manifest
              </Button>
            )}
          </h1>
        </Col>
      </Row>

      <Row>
        <Form>
          <Row>
            <Col sm={4} md={3} lg={3} xl={2}>
              <ProductLine
                size="sm"
                onChange={async (newModel) => {
                  if (undefined !== newModel && newModel !== model) {
                    setModel(newModel);
                  }
                }}
                value={model}
              />
            </Col>
          </Row>
        </Form>
      </Row>

      <Row>
        {loading ? (
          <p>Loading. Please wait...</p>
        ) : (
          <Table striped hover>
            <thead>
              <tr>
                <th>Version</th>
                <th>Release Files</th>
                <th>Debug Files</th>
                <th>Log Decoder Files</th>
                <th>Release Notes</th>
                <th>Build Author</th>
                <th>Time</th>
                <th>Source</th>
              </tr>
            </thead>

            <tbody>
              {releases?.map((release) => (
                <ReleaseTableRow
                  key={`fw-${release.id}`}
                  release={release}
                  model={model}
                  jwt={jwt}
                  editReleaseNotesClicked={() => {
                    setShowReleaseNotesModal(true);
                    setReleaseToEdit(release);
                  }}
                />
              ))}
            </tbody>
          </Table>
        )}
      </Row>

      <SubmitNewReleaseModal
        show={showSubmitNewReleaseModal}
        close={() => setShowSubmitNewReleaseModal(false)}
        onSubmit={async (buildUrl) => {
          setLoading(true);
          setShowSubmitNewReleaseModal(false);
          try {
            await HTTP_CLIENT.post({
              path: "/api/v2/build/download",
              query: { buildUrl },
              headers: { Authorization: `Bearer ${jwt}` },
            });
            await updateList(model);
          } catch (e: any) {
            console.error(e.stack);
            toast.error(e.message);
          } finally {
            setLoading(false);
          }
        }}
      />

      <SubmitNewBundleModal
        show={showSubmitNewBundleModal}
        close={() => setShowSubmitNewBundleModal(false)}
        onSubmit={async ({ bundleJson, version }) => {
          setLoading(true);
          setShowSubmitNewBundleModal(false);
          try {
            await submitNewBundle(bundleJson, version);
          } catch (e: any) {
            console.error(e.stack);
            toast.error(e.message);
          } finally {
            setLoading(false);
            await updateList(model);
          }
        }}
      />

      {jwt && (
        <ReleaseNotesModal
          show={showReleaseNotesModal}
          onHide={() => {
            setShowReleaseNotesModal(false);
            setReleaseToEdit(undefined);
          }}
          jwt={jwt}
          onSubmit={async (content) => {
            if (content !== releaseToEdit!.releaseNotes) {
              await updateReleaseNotes(content);
            }
            setShowReleaseNotesModal(false);
          }}
          initialContent={releaseToEdit?.releaseNotes || null}
        />
      )}

      <GenerateManifestModal
        show={showGenerateManifestModal}
        onHide={() => setShowGenerateManifestModal(false)}
      />
    </>
  );
};
